/**
 * Viral Moment Scheduler — purely declarative timeline of pre-defined
 * moments that creators / ops can amplify around a kickoff time.
 * Pure JS: returns offsets relative to a `kickoffAt` ISO string.
 */
export type ViralMomentKind =
  | "t_minus_24h"
  | "kickoff_countdown"
  | "halftime_war"
  | "last_minute"
  | "penalty_drama"
  | "victory_burst";

export interface ViralMoment {
  kind: ViralMomentKind;
  label: string;
  emoji: string;
  offsetMin: number; // relative to kickoff
  durationMin: number;
  cta: string;
}

const SCHEDULE: ViralMoment[] = [
  { kind: "t_minus_24h", label: "24h antes", emoji: "📣", offsetMin: -24 * 60, durationMin: 30, cta: "Lanza el countdown" },
  { kind: "kickoff_countdown", label: "Kickoff -10m", emoji: "🚀", offsetMin: -10, durationMin: 10, cta: "Comparte tensión" },
  { kind: "halftime_war", label: "Halftime", emoji: "⚔️", offsetMin: 50, durationMin: 15, cta: "Activa nation war" },
  { kind: "last_minute", label: "Last minute", emoji: "⏱️", offsetMin: 85, durationMin: 10, cta: "Hype final" },
  { kind: "penalty_drama", label: "Penalty drama", emoji: "😱", offsetMin: 115, durationMin: 15, cta: "Comparte drama" },
  { kind: "victory_burst", label: "Victory burst", emoji: "🏆", offsetMin: 125, durationMin: 30, cta: "Celebra ganador" },
];

export function getViralSchedule(): ViralMoment[] { return SCHEDULE; }

export function viralScheduleFor(kickoffISO: string) {
  const kickoff = new Date(kickoffISO).getTime();
  if (Number.isNaN(kickoff)) return [];
  return SCHEDULE.map((m) => ({
    ...m,
    startAt: new Date(kickoff + m.offsetMin * 60_000).toISOString(),
    endAt: new Date(kickoff + (m.offsetMin + m.durationMin) * 60_000).toISOString(),
  }));
}

export function statusFor(startAt: string, endAt: string, now = Date.now()): "upcoming" | "live" | "past" {
  const s = new Date(startAt).getTime();
  const e = new Date(endAt).getTime();
  if (now < s) return "upcoming";
  if (now > e) return "past";
  return "live";
}

export function defaultKickoffISO(): string {
  // Default = next sunday 14:00 local — usable for demos before a real fixture lands.
  const d = new Date();
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7 || 7));
  d.setHours(14, 0, 0, 0);
  return d.toISOString();
}